import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Cookie, Download, FileText, MessageCircle, UtensilsCrossed, LayoutGrid, List, CalendarPlus } from "lucide-react";
import { PLATTERS, PARTY_PACKS, KIDS_FEASTS } from "@/lib/site";
import Mascot from "@/components/Mascot";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const slugOf = (url) => (url.split("/product/")[1] || "").replace(/\/+$/, "");

function MenuRow({ item, img, listView, items, photoFor }) {
  const [open, setOpen] = useState(false);
  const [viewItem, setViewItem] = useState(item);
  const photo = photoFor ? photoFor(viewItem) : viewItem.img || img;
  const nav = (d) => {
    if (!items || items.length < 2) return;
    const idx = items.findIndex((i) => i.id === viewItem.id);
    setViewItem(items[(idx + d + items.length) % items.length]);
  };

  const nameBtn = (
    <button
      onClick={() => { setViewItem(item); setOpen(true); }}
      className="flex items-start gap-1 text-left"
      data-testid={`combo-name-${item.id}`}
      title="Tap to see what's inside"
    >
      <span className={`text-sm font-extrabold leading-snug transition-colors ${listView ? "text-[#241c0d] group-hover:text-[#c87700]" : "text-[#fff7e8] group-hover:text-[#f8b818]"}`}>
        {item.name}
      </span>
      <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }} className="shrink-0 text-[#c87700]">
        <ChevronDown size={13} strokeWidth={3} />
      </motion.span>
    </button>
  );

  const infoDialog = (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setViewItem(item); // reopening shows the tapped pack again, not the last one browsed
      }}
    >
      <DialogContent
        className="max-w-sm overflow-hidden rounded-3xl border-[#241c0d]/10 !bg-[#fff7e8] !text-[#241c0d] p-0 [&>button]:!right-3 [&>button]:!top-3 [&>button]:!z-10 [&>button]:!rounded-full [&>button]:!bg-[#fff7e8] [&>button]:!p-1.5 [&>button]:!text-[#241c0d] [&>button]:!opacity-100 [&>button]:!shadow-[0_4px_14px_rgba(36,28,13,0.35)]"
        aria-describedby={undefined}
        data-testid={`combo-modal-${viewItem.id}`}
      >
        {photo && (
          <div className="relative">
            <img
              src={photo}
              alt={`${viewItem.name} — what's inside`}
              className="h-40 w-full object-cover"
              data-testid={`combo-modal-img-${viewItem.id}`}
            />
            {items && items.length > 1 && (
              <>
                <button
                  onClick={() => nav(-1)}
                  aria-label="Previous pack"
                  className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-[#fff7e8]/95 text-[#241c0d] shadow-[0_4px_14px_rgba(36,28,13,0.4)] transition-transform hover:scale-110"
                  data-testid="combo-modal-prev-btn"
                >
                  <ChevronLeft size={18} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => nav(1)}
                  aria-label="Next pack"
                  className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-[#fff7e8]/95 text-[#241c0d] shadow-[0_4px_14px_rgba(36,28,13,0.4)] transition-transform hover:scale-110"
                  data-testid="combo-modal-next-btn"
                >
                  <ChevronRight size={18} strokeWidth={2.5} />
                </button>
                <span className="absolute bottom-2 left-1/2 z-10 -translate-x-1/2 rounded-full bg-[#14120c]/70 px-2.5 py-0.5 text-[10px] font-extrabold text-[#fff7e8]" data-testid="combo-modal-count">
                  {items.findIndex((i) => i.id === viewItem.id) + 1} / {items.length}
                </span>
              </>
            )}
          </div>
        )}
        <div className="p-6">
          <DialogTitle className="font-display text-2xl leading-tight text-[#241c0d]">{viewItem.name}</DialogTitle>
          <p className="font-display text-xl text-[#c87700]">{viewItem.price}</p>
          <ul className="mt-3 space-y-1.5">
            {viewItem.desc.map((line) => (
              <li key={line} className="flex items-start gap-1.5 text-xs font-semibold text-[#241c0d]">
                <Check size={12} strokeWidth={3} className="mt-0.5 shrink-0 text-[#c87700]" />
                {line}
              </li>
            ))}
          </ul>
          {viewItem.halaal && <p className="mt-2 text-[11px] italic text-[#8a7b5e]">* {viewItem.halaal}</p>}
          {viewItem.bookingOnly && (
            <p className="mt-3 rounded-xl bg-[#f8b818]/15 p-3 text-[11px] leading-relaxed text-[#8a7b5e]">
              Party packs are ordered with your party booking — please give 2 days' notice. Email{" "}
              <a href="mailto:janet@coolrunnings.capetown" className="font-extrabold text-[#c87700] underline underline-offset-2">
                janet@coolrunnings.capetown
              </a>{" "}
              and we'll add them to your table.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  if (listView) {
    return (
      <div className="border-b border-[#241c0d]/10 py-3" data-testid={`menu-item-${item.id}`}>
        <div className="flex items-center justify-between gap-3">
          {nameBtn}
          <span className="font-display text-xl text-[#c87700]">{item.price}</span>
        </div>
        {infoDialog}
      </div>
    );
  }
  return (
    <div
      className="group flex flex-col justify-between gap-3 rounded-3xl border-2 border-[#f8b818]/35 bg-[#14120c] px-5 py-4 shadow-[0_14px_35px_rgba(36,28,13,0.22)] transition-all hover:-translate-y-1 hover:border-[#f8b818]"
      data-testid={`menu-item-${item.id}`}
    >
      <span className="flex min-w-0 items-start gap-3">
        {photo ? (
          <img
            src={photo}
            alt={item.name}
            loading="lazy"
            className="h-8 w-8 shrink-0 rounded-full object-cover"
            data-testid={`menu-thumb-${item.id}`}
          />
        ) : (
          <span className="rounded-full bg-[#f8b818]/20 p-2 text-[#f8b818]">
            <UtensilsCrossed size={15} />
          </span>
        )}
        {nameBtn}
      </span>
      <span className="flex items-center justify-between gap-3 pl-11">
        <span className="font-display shrink-0 text-xl text-[#f8b818]">{item.price}</span>
      </span>
      {infoDialog}
    </div>
  );
}

const PAMPHLET_URL = "/birthday-pamphlet.pdf";
const PAMPHLET_SHARE_TEXT =
  "Check out the Toboggan Cape Town birthday party pamphlet: https://toboggan-capetown.emergent.host/birthday-pamphlet.pdf";

// try to share the ACTUAL pamphlet file first (phone share sheet attaches the PDF);
// fall back to a plain wa.me link where file sharing is not supported
const sharePamphlet = async () => {
  const fallback = () => window.open(`https://wa.me/?text=${encodeURIComponent(PAMPHLET_SHARE_TEXT)}`, "_blank", "noopener,noreferrer");
  try {
    const res = await fetch(PAMPHLET_URL);
    if (!res.ok) return fallback();
    const blob = await res.blob();
    const file = new File([blob], "birthday-pamphlet.pdf", { type: "application/pdf" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "Toboggan Cape Town birthday party pamphlet", text: PAMPHLET_SHARE_TEXT });
    } else {
      fallback();
    }
  } catch (err) {
    if (err && err.name !== "AbortError") fallback();
  }
};

export default function Platters() {
  const navigate = useNavigate();
  const [menuImages, setMenuImages] = useState({});
  const [listView, setListView] = useState(false);
  const [showRudi, setShowRudi] = useState(false);
  const [liveItems, setLiveItems] = useState([]);

  useEffect(() => {
    api.get("/menu-images")
      .then((res) => setMenuImages(res.data || {}))
      .catch(() => setMenuImages({}));
    api.get("/menu-items")
      .then((res) => setLiveItems(res.data || []))
      .catch(() => setLiveItems([]));
  }, []);

  // staff edits from the admin portal override the static defaults
  const live = Object.fromEntries(liveItems.map((i) => [i.id, i]));
  const applyLive = (item, category) => {
    const l = live[item.id];
    if (!l) return item;
    const desc =
      category === "platter"
        ? l.lines?.length
          ? l.lines.join(", ")
          : item.desc
        : l.lines?.length
          ? l.lines
          : item.desc;
    return { ...item, name: l.name || item.name, price: l.price || item.price, desc, halaal: l.halaal !== undefined ? l.halaal : item.halaal };
  };
  const shownIn = (item, category) => live[item.id]?.visible !== false;
  const livePlatters = PLATTERS.filter((p) => shownIn(p, "platter")).map((p) => applyLive(p, "platter"));
  const livePacks = PARTY_PACKS.filter((p) => shownIn(p, "combo")).map((p) => applyLive(p, "combo"));
  const liveFeasts = KIDS_FEASTS.filter((p) => shownIn(p, "feast")).map((p) => applyLive(p, "feast"));

  return (
    <section id="platters" className="border-y border-[#241c0d]/10 bg-[#fff1d9] py-24" data-testid="platters-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <h2 className="max-w-2xl text-4xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)] sm:text-5xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>THE FULL MENU</h2>
          </div>
          <button
            onClick={() => setShowRudi(true)}
            title="Click me!"
            className="hidden cursor-pointer rounded-2xl border border-[#241c0d]/10 bg-white/60 p-2 shadow-[0_10px_30px_rgba(36,28,13,0.12)] transition-transform hover:scale-105 lg:block"
            data-testid="platters-kiosk-photo"
          >
            <img
              src="/logo.png"
              alt="Cool Runnings Cape Town logo"
              className="h-24 w-auto object-contain"
              loading="lazy"
            />
          </button>
        </motion.div>

        {showRudi && (
          <Mascot
            fixed
            side="right"
            className="bottom-6 right-60 z-[85]"
            greet
            phrases={["Hungry? The platters here are legendary!"]}
          />
        )}

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {livePlatters.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              className="group flex flex-col overflow-hidden rounded-3xl border-2 border-[#f8b818]/35 bg-[#14120c] shadow-[0_14px_35px_rgba(36,28,13,0.22)] transition-all hover:-translate-y-1.5 hover:border-[#f8b818]"
              data-testid={`platter-card-${p.id}`}
            >
              <div className="relative h-48 shrink-0 overflow-hidden">
                <img
                  src={p.img}
                  alt={`${p.name} from Cool Runnings`}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-2xl leading-tight text-[#f8b818]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>{p.name}</h3>
                  <span className="font-display shrink-0 text-2xl text-[#f8b818]" data-testid={`platter-price-${p.id}`}>{p.price}</span>
                </div>
                {p.halaal && (
                  <span className="chip mt-3 !px-3 !py-1 !text-[10px] !border-green-400/40 !text-green-400">{typeof p.halaal === "string" ? p.halaal : "Halaal"}</span>
                )}
                <p className="mt-2 text-sm text-[#fff7e8]/75">{p.desc}</p>
                <span
                  className="mt-auto inline-flex items-center gap-2 self-start rounded-full border border-[#fff7e8]/30 px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-[#fff7e8]"
                  data-testid={`platter-booking-only-${p.id}`}
                >
                  <CalendarPlus size={14} className="text-[#f8b818]" /> 4 days' notice · full payment
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Birthday Packages pamphlet — download it, share it, or book on WhatsApp in one tap */}
        <div className="mt-14 rounded-3xl border-2 border-[#f8b818]/50 bg-[#14120c] p-6 shadow-[0_14px_35px_rgba(36,28,13,0.22)] sm:p-8" data-testid="birthday-packages-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <a
              href={PAMPHLET_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex h-44 w-full max-w-[190px] items-center justify-center rounded-2xl bg-[#faf5ea] p-2"
              title="Open the pamphlet"
              data-testid="birthday-packages-thumb"
            >
              <img
                src="/pamphlet-birthday-front.png"
                alt="Birthday Packages pamphlet"
                className="h-full w-full rounded-xl object-contain transition-transform group-hover:scale-[1.03]"
                loading="lazy"
                data-testid="birthday-packages-thumb-img"
              />
            </a>
            <div className="max-w-xl min-w-[240px] flex-1">
              <h3 className="text-2xl leading-tight text-[#f8b818]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>Birthday Packages</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-[#fff7e8]/70">
                Three party packages from R380 per person — the pamphlet shows exactly what each includes (offerings
                apply to a minimum of 5 people).
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={PAMPHLET_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold !px-4 !py-2.5 !text-xs"
                data-testid="birthday-packages-download-btn"
              >
                <Download size={13} /> Open the pamphlet
              </a>
              <button
                type="button"
                onClick={sharePamphlet}
                className="btn-gold !px-4 !py-2.5 !text-xs"
                data-testid="birthday-packages-whatsapp-btn"
              >
                <MessageCircle size={13} /> Share on WhatsApp
              </button>
            </div>
          </div>

          {/* the moms' fast lane: removed — the party booking now flows through janet@ and the pamphlet */}
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap items-center gap-3 text-left text-3xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)]"
            style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}
            data-testid="party-packs-heading"
          >
            <span className="flex items-center gap-3">
              <Cookie size={26} /> Party Pack Combos <span className="text-xl text-[#8a7b5e]">from R60</span>
            </span>
            <a
              href={PAMPHLET_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold !px-3.5 !py-1.5 !text-[10px]"
              data-testid="party-packs-view-pamphlet-btn"
            >
              <FileText size={11} /> View Pamphlet
            </a>
            <button
              type="button"
              onClick={sharePamphlet}
              className="btn-gold !px-3.5 !py-1.5 !text-[10px]"
              data-testid="party-packs-share-whatsapp-btn"
            >
              <MessageCircle size={11} /> Share on WhatsApp
            </button>
          </motion.div>
          <div className="glass flex items-center gap-1 rounded-full p-1" data-testid="menu-view-toggle">
            <button
              onClick={() => setListView(false)}
              className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${!listView ? "bg-[#f8b818] text-[#0a0a0b]" : "text-[#241c0d]/60 hover:text-[#c87700]"}`}
              aria-label="Card view"
              title="Card view"
              data-testid="menu-view-cards-btn"
            >
              <LayoutGrid size={14} />
            </button>
            <button
              onClick={() => setListView(true)}
              className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${listView ? "bg-[#f8b818] text-[#0a0a0b]" : "text-[#241c0d]/60 hover:text-[#c87700]"}`}
              aria-label="List view"
              title="List view"
              data-testid="menu-view-list-btn"
            >
              <List size={14} />
            </button>
          </div>
        </div>
        {listView ? (
          <div className="mt-2 border-t border-[#241c0d]/10" data-testid="party-packs-list">
            {livePacks.map((p) => (
              <MenuRow key={p.id} item={p} listView items={livePacks} photoFor={(i) => i.img || menuImages[slugOf(i.url)] || i.fallbackImg} />
            ))}
          </div>
        ) : (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="party-packs-grid">
            {livePacks.map((p) => (
              <MenuRow key={p.id} item={p} img={p.img || menuImages[slugOf(p.url)] || p.fallbackImg} items={livePacks} photoFor={(i) => i.img || menuImages[slugOf(i.url)] || i.fallbackImg} />
            ))}
          </div>
        )}
        <p className="mt-4 text-sm leading-relaxed text-[#3d362c]" data-testid="party-packs-notice">
          Questions about a booking? Email{" "}
          <a
            href="mailto:janet@coolrunnings.capetown"
            className="font-extrabold text-[#c87700] underline underline-offset-2"
            data-testid="party-packs-janet-link"
          >
            janet@coolrunnings.capetown
          </a>
          .
        </p>

        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6 }}
          className="mt-12 flex items-center gap-3 text-3xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)]"
          style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}
          data-testid="kids-feasts-heading"
        >
          <UtensilsCrossed size={26} /> Party Pack Menu Kids <span className="text-xl text-[#8a7b5e]">from R125</span>
        </motion.h3>
        {listView ? (
          <div className="mt-2 border-t border-[#241c0d]/10" data-testid="kids-feasts-list">
            {liveFeasts.map((p) => (
              <MenuRow key={p.id} item={p} listView items={liveFeasts} photoFor={(i) => i.img || menuImages[slugOf(i.url)]} />
            ))}
          </div>
        ) : (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="kids-feasts-grid">
            {liveFeasts.map((p) => (
              <MenuRow key={p.id} item={p} img={p.img || menuImages[slugOf(p.url)]} items={liveFeasts} photoFor={(i) => i.img || menuImages[slugOf(i.url)]} />
            ))}
          </div>
        )}
        <motion.button
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6 }}
          onClick={() => navigate("/contact", { state: { enquiryType: "Platter Orders" } })}
          className="card-dark group mt-14 flex w-full flex-col overflow-hidden text-left transition-all hover:-translate-y-1.5 hover:border-[#f8b818] sm:flex-row"
          data-testid="platters-event-enquiry-card"
        >
          <div className="relative h-48 overflow-hidden sm:h-auto sm:w-1/3 sm:shrink-0">
            <img
              src="/products/platters-event.jpg"
              alt="Families and kids enjoying platters at the park's log benches"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#241c0d]/50 to-transparent" />
          </div>
          <div className="flex flex-1 flex-col justify-center p-8">
            <h3 className="text-3xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>Planning a big event?</h3>
            <p className="mt-2 max-w-lg text-sm text-[#8a7b5e]">
              Platters, party packs and feasts scale from birthday tables to corporate days. Talk to our front
              desk and we'll set it all up.
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-extrabold uppercase tracking-wide text-[#c87700]">
              Enquire now <ArrowUpRight size={15} />
            </span>
          </div>
        </motion.button>
      </div>
    </section>
  );
}
